import { Link } from "react-router-dom"
import { Sparkles } from "lucide-react"
import { AskDynamoPawIllustration } from "~/components/AskDynamoPawIllustration"
import { dogPossessivePronoun } from "~/utils/dogPronouns"
import { trackEvent } from "~/utils/trackEvent"

interface DashboardAskDynamoCardProps {
  dogName: string
  dogSex?: "male" | "female" | null
  dailyRemaining: number | null
  dailyLimit: number | null
  showPlusCta?: boolean
}

export function DashboardAskDynamoCard({
  dogName,
  dogSex,
  dailyRemaining,
  dailyLimit: _dailyLimit,
  showPlusCta = false,
}: DashboardAskDynamoCardProps) {
  function handleCtaClick() {
    trackEvent("ask_dynamo_now_tap", { dogName })
  }

  const isUnlimited = dailyRemaining === null
  const possessive = dogPossessivePronoun(dogSex)

  return (
    <section
      className="mt-4 rounded-2xl border border-[var(--color-ask-dynamo)]/15 bg-[var(--color-ask-dynamo-dashboard-bg)] p-4 shadow-[0_6px_24px_rgba(108,61,242,0.08)]"
      aria-labelledby="dashboard-ask-dynamo-heading"
    >
      <div className="flex items-start gap-3">
        <AskDynamoPawIllustration className="h-12 w-12 shrink-0" />
        <div className="min-w-0 flex-1">
          <h2
            id="dashboard-ask-dynamo-heading"
            className="text-base font-semibold text-[var(--color-ask-dynamo-muted)]"
          >
            Ask Dynamo
          </h2>
          <p className="text-sm font-medium text-[var(--color-ask-dynamo)]">
            Your personal dog coach
          </p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--color-text-secondary)]">
            Ask questions about {dogName}&apos;s behaviour and get guidance tailored to {possessive}{" "}
            unique personality.
          </p>
        </div>
        {!isUnlimited && dailyRemaining !== null && (
          <div
            className="shrink-0 rounded-lg border border-[var(--color-ask-dynamo)]/20 bg-white/70 px-2.5 py-2 text-center"
            aria-label={`${dailyRemaining} questions left today`}
          >
            <p className="text-2xl font-bold leading-none text-[var(--color-ask-dynamo)]">
              {dailyRemaining}
            </p>
            <p className="mt-0.5 text-[10px] leading-tight text-[var(--color-ask-dynamo-muted)]">
              questions
              <br />
              left today
            </p>
          </div>
        )}
      </div>

      <Link
        to="/ask-dynamo"
        onClick={handleCtaClick}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-ask-dynamo)] px-4 py-3 text-sm font-medium text-white transition hover:opacity-90"
      >
        <Sparkles size={16} aria-hidden />
        Ask Dynamo Now
      </Link>

      {showPlusCta && (
        <Link
          to="/plus"
          data-testid="dashboard-ask-dynamo-plus-cta"
          className="mt-2 flex w-full min-h-11 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-ask-dynamo)]/40 bg-[var(--color-ask-dynamo-surface)] px-4 py-2.5 text-base font-semibold text-[var(--color-ask-dynamo-muted)] hover:border-[var(--color-ask-dynamo)] hover:text-[var(--color-ask-dynamo)]"
        >
          Discover Doggy Dynamo Plus →
        </Link>
      )}
    </section>
  )
}
